import type { ReactNode } from 'react';

type Variant =
  | 'projects'
  | 'tasks'
  | 'plans'
  | 'notes'
  | 'expenses'
  | 'feed'
  | 'team'
  | 'chat'
  | 'generic';

const STROKE = 'currentColor';
// Akcent vzhľadu (tokeny). Cez style, nie atribút: var() v prezentačnom
// atribúte SVG prehliadače nečítajú spoľahlivo.
const ACCENT = 'rgb(var(--ww-brand-500))';
const GREEN = '#34c759';

// Minimal line-art illustrations (96×96), brand accents, no text baked in.
const ART: Record<Variant, ReactNode> = {
  projects: (
    <>
      <path
        d="M14 34c0-4 3-7 7-7h14l8 8h25c4 0 7 3 7 7v24c0 4-3 7-7 7H21c-4 0-7-3-7-7V34Z"
        fill="none"
        stroke={STROKE}
        strokeWidth="4"
        strokeLinejoin="round"
      />
      <path d="M48 44v16M40 52h16" style={{ stroke: ACCENT }} strokeWidth="4" strokeLinecap="round" />
    </>
  ),
  tasks: (
    <>
      <rect x="22" y="16" width="52" height="64" rx="10" fill="none" stroke={STROKE} strokeWidth="4" />
      <path d="M34 38h28M34 50h28M34 62h16" stroke={STROKE} strokeWidth="4" strokeLinecap="round" opacity="0.4" />
      <circle cx="66" cy="66" r="14" fill={GREEN} opacity="0.15" />
      <path d="M60 66l4 4 8-8" fill="none" stroke={GREEN} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  plans: (
    <>
      <rect x="16" y="22" width="64" height="58" rx="10" fill="none" stroke={STROKE} strokeWidth="4" />
      <path d="M16 38h64" stroke={STROKE} strokeWidth="4" />
      <path d="M32 16v12M64 16v12" stroke={STROKE} strokeWidth="4" strokeLinecap="round" />
      <rect x="26" y="48" width="20" height="12" rx="4" style={{ fill: ACCENT }} opacity="0.8" />
      <rect x="52" y="62" width="18" height="10" rx="4" fill={GREEN} opacity="0.6" />
    </>
  ),
  notes: (
    <>
      <path
        d="M22 18h44c4 0 8 4 8 8v30L56 74H30c-4 0-8-4-8-8V26c0-4 4-8 8-8Z"
        fill="none"
        stroke={STROKE}
        strokeWidth="4"
        strokeLinejoin="round"
      />
      <path d="M74 56H56v18" fill="none" stroke={STROKE} strokeWidth="4" strokeLinejoin="round" />
      <path d="M34 36h28M34 48h18" style={{ stroke: ACCENT }} strokeWidth="4" strokeLinecap="round" opacity="0.7" />
    </>
  ),
  expenses: (
    <>
      <circle cx="48" cy="48" r="30" fill="none" stroke={STROKE} strokeWidth="4" />
      <path
        d="M58 38c-2.5-3-6-5-10-5-7 0-13 7-13 15s6 15 13 15c4 0 7.5-2 10-5M30 44h18M30 52h18"
        fill="none"
        style={{ stroke: ACCENT }}
        strokeWidth="4"
        strokeLinecap="round"
      />
    </>
  ),
  feed: (
    <>
      <rect x="18" y="20" width="60" height="16" rx="8" fill="none" stroke={STROKE} strokeWidth="4" opacity="0.35" />
      <rect x="18" y="42" width="60" height="16" rx="8" fill="none" stroke={STROKE} strokeWidth="4" opacity="0.6" />
      <rect x="18" y="64" width="42" height="16" rx="8" fill="none" style={{ stroke: ACCENT }} strokeWidth="4" />
    </>
  ),
  team: (
    <>
      <circle cx="36" cy="36" r="12" fill="none" stroke={STROKE} strokeWidth="4" />
      <path d="M16 74c2-12 10-18 20-18s18 6 20 18" fill="none" stroke={STROKE} strokeWidth="4" strokeLinecap="round" />
      <circle cx="66" cy="40" r="9" fill="none" style={{ stroke: ACCENT }} strokeWidth="4" />
      <path d="M58 70c2-8 7.5-13 14.5-13 3 0 5.5 0.8 7.5 2.2" fill="none" style={{ stroke: ACCENT }} strokeWidth="4" strokeLinecap="round" />
    </>
  ),
  chat: (
    <>
      <path
        d="M20 28c0-5 4-9 9-9h38c5 0 9 4 9 9v22c0 5-4 9-9 9H44L28 72V59h-8V28Z"
        transform="translate(0 4)"
        fill="none"
        stroke={STROKE}
        strokeWidth="4"
        strokeLinejoin="round"
      />
      <circle cx="38" cy="44" r="3" style={{ fill: ACCENT }} />
      <circle cx="50" cy="44" r="3" style={{ fill: ACCENT }} />
      <circle cx="62" cy="44" r="3" style={{ fill: ACCENT }} />
    </>
  ),
  generic: (
    <>
      <circle cx="48" cy="48" r="28" fill="none" stroke={STROKE} strokeWidth="4" opacity="0.5" />
      <path d="M48 30v10M48 56v10M30 48h10M56 48h10" style={{ stroke: ACCENT }} strokeWidth="4" strokeLinecap="round" />
    </>
  ),
};

/**
 * Friendly empty state with a line-art illustration. Use instead of bare
 * "Žiadne…" paragraphs:
 *
 *   <EmptyState variant="tasks" title="Žiadne tasky"
 *     hint="Pridaj prvý task tlačidlom hore." />
 */
export const EmptyState = ({
  variant = 'generic',
  title,
  hint,
  action,
}: {
  variant?: Variant;
  title: string;
  hint?: string;
  action?: ReactNode;
}) => (
  <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
    <svg
      viewBox="0 0 96 96"
      className="h-20 w-20 text-slate-300 dark:text-slate-600"
      aria-hidden="true"
    >
      {ART[variant]}
    </svg>
    <p className="t-headline text-slate-800 dark:text-slate-100">{title}</p>
    {/* Nápoveda je to podstatné — hovorí, čo spraviť. slate-400 na bielej
        (a slate-500 na čiernej) mala kontrast pod 3 : 1 a čítala sa ako
        vodoznak. Šírka obmedzená, aby sa na monitore nerozliala do riadka
        cez celú stránku. */}
    {hint ? <p className="-mt-1.5 max-w-sm text-[13px] leading-relaxed text-slate-500 dark:text-slate-400">{hint}</p> : null}
    {action ? <div className="mt-1">{action}</div> : null}
  </div>
);
